import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, FormControl, InputLabel,
  Select, MenuItem, Chip, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { listarDiscipulados, deletarDiscipulado } from '../../api/discipulado';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';

const STATUS_COLOR = {
  ativo: 'success',
  pausado: 'warning',
  concluido: 'info',
  cancelado: 'error'
};
const STATUS_LABEL = {
  ativo: 'Ativo',
  pausado: 'Pausado',
  concluido: 'Concluído',
  cancelado: 'Cancelado'
};

function DiscipuladoLista() {
  const navigate = useNavigate();
  const [discipulados, setDiscipulados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      const data = await listarDiscipulados(params);
      setDiscipulados(data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar discipulados');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, [filtroStatus]);

  const handleDeletar = async () => {
    if (!confirmDel) return;
    try {
      await deletarDiscipulado(confirmDel.id);
      setConfirmDel(null);
      carregar();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao deletar discipulado');
      setConfirmDel(null);
    }
  };

  const formatarData = (data) => {
    if (!data) return '—';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  return (
    <>
      <Header />
      <ContextNavBar context="discipulado" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>Discipulados</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/discipulados/novo')}>
            Novo Discipulado
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filtroStatus} label="Status" onChange={e => setFiltroStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_LABEL).map(([k, lbl]) => (
                <MenuItem key={k} value={k}><Chip label={lbl} color={STATUS_COLOR[k]} size="small" /></MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : erro ? (
          <Alert severity="error">{erro}</Alert>
        ) : (
          <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Discipulador</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Discipulando</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data Início</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {discipulados.map((d) => (
                  <TableRow
                    key={d.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/discipulados/${d.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.discipulador_nome || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{d.discipulando_nome || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABEL[d.status] || d.status} color={STATUS_COLOR[d.status] || 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatarData(d.data_inicio)}</Typography>
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Deletar">
                        <IconButton size="small" color="error" onClick={() => setConfirmDel(d)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {discipulados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Alert severity="info" sx={{ my: 1 }}>Nenhum discipulado encontrado.</Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Deletar Discipulado</DialogTitle>
          <DialogContent>
            <Typography>
              Deletar discipulado de "{confirmDel?.discipulador_nome}" com "{confirmDel?.discipulando_nome}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDel(null)}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={handleDeletar}>Confirmar</Button>
          </DialogActions>
        </Dialog>
      </Container>
      <Footer />
    </>
  );
}

export default DiscipuladoLista;
